import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { uploadImage, deleteImage, extractFilePathFromUrl } from '../../utils/supabaseStorage';
import { useAuth } from '../../hooks/useAuth';

interface ImageUploadProps {
  imageUrl: string | null;
  onImageChange: (url: string | null) => void;
  disabled?: boolean;
  className?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  imageUrl,
  onImageChange,
  disabled = false,
  className = ''
}) => {
  const { user } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!file.type.startsWith('image/')) {
      return 'Please select an image file (JPEG, PNG, WebP, or GIF)';
    }

    // Check file size (5MB limit)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return 'Image must be smaller than 5MB';
    }

    return null;
  };

  const handleFileUpload = useCallback(async (file: File) => {
    setUploadError(null);
    setIsUploading(true);

    if (!user) {
      setUploadError('You must be logged in to upload images');
      setIsUploading(false);
      return;
    }

    try {
      // Validate file
      const validationError = validateFile(file);
      if (validationError) {
        setUploadError(validationError);
        setIsUploading(false);
        return;
      }

      // Delete old image if it exists
      if (imageUrl) {
        try {
          const oldPath = extractFilePathFromUrl(imageUrl);
          if (oldPath) {
            await deleteImage(oldPath);
          }
        } catch (error) {
          console.warn('Failed to delete old image:', error);
          // Continue with upload even if deletion fails
        }
      }
      
      // Upload to Supabase Storage
      const result = await uploadImage(file, user.id, 'templates');
      onImageChange(result.url);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  }, [onImageChange, user, imageUrl]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    if (disabled) return;

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleRemoveImage = async () => {
    if (imageUrl) {
      try {
        const filePath = extractFilePathFromUrl(imageUrl);
        if (filePath) {
          await deleteImage(filePath);
        }
      } catch (error) {
        console.warn('Failed to delete image:', error);
        // Continue with removal even if deletion fails
      }
    }
    
    onImageChange(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Design Image
        </label>
        {imageUrl && (
          <button
            type="button"
            onClick={handleRemoveImage}
            disabled={disabled}
            className="flex items-center px-3 py-1 text-sm text-red-600 dark:text-red-400 
              hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 
              dark:hover:bg-red-900/20 rounded-lg transition-all duration-300 
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="h-4 w-4 mr-1" />
            Remove
          </button>
        )}
      </div>

      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-xl transition-all duration-300 ${
          dragActive // Keep this logic
            ? 'border-fire-400 bg-fire-50 dark:bg-fire-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-fire-300 dark:hover:border-fire-600'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={handleUploadClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          disabled={disabled}
          className="hidden"
        />

        {imageUrl ? (
          /* Image Preview */ // Keep this comment
          <div className="relative group">
            <img
              src={imageUrl}
              alt="Design preview"
              className="w-full h-48 object-cover rounded-lg"
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 
              transition-opacity duration-300 rounded-lg flex items-center justify-center">
              <div className="text-white text-center">
                <Upload className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm font-medium">Click to change image</p>
              </div>
            </div>
          </div>
        ) : (
          /* Upload Prompt */
          <div className="p-12 text-center"> {/* Keep this div */}
            {isUploading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="h-12 w-12 text-fire-500 animate-spin mb-4" />
                <p className="text-gray-600 dark:text-gray-400 font-medium">
                  Uploading image...
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-fire-100 dark:bg-fire-900/30 rounded-full 
                  flex items-center justify-center mb-4"> {/* Keep this div */}
                  <ImageIcon className="h-8 w-8 text-modular-frosted-indigo dark:text-modular-frosted-indigo" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2">
                  Upload Design Image
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 max-w-sm">
                  Add an image of your design so the AI can understand the visual context 
                  and create more relevant listings
                </p>
                <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                  <span>Drag & drop or click to select</span>
                  <span>•</span>
                  <span>Max 5MB</span>
                  <span>•</span>
                  <span>JPG, PNG, WebP, GIF</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {uploadError && (
        <div className="flex items-start space-x-2 p-3 bg-red-50 dark:bg-red-900/20 
          border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-700 dark:text-red-400 text-sm font-medium">
              Upload Error
            </p>
            <p className="text-red-600 dark:text-red-400 text-sm">
              {uploadError}
            </p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {imageUrl && !uploadError && (
        <div className="flex items-start space-x-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 
          border border-emerald-200 dark:border-emerald-800 rounded-lg">
          <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-emerald-700 dark:text-emerald-400 text-sm font-medium">
              Image Ready
            </p>
            <p className="text-emerald-600 dark:text-emerald-400 text-sm">
              Your design image will be used to provide visual context to the AI
            </p>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 
        rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <ImageIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-700 dark:text-blue-300 text-sm font-medium mb-1">
              Why add an image?
            </p>
            <p className="text-blue-600 dark:text-blue-400 text-sm">
              The AI will analyze your design image to create more accurate and relevant 
              listing descriptions that match your product's visual style, colors, and features.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};